export type QualityResult = {
  score: number;        // 0..100
  median: number | null;
  samples: number;
  pctUnderGood: number; // percent samples under `good` meters
  pctUnderAccept: number; // percent samples under `acceptable` meters
};

function median(values: number[]): number | null {
  const v = values.slice().sort((a,b)=>a-b);
  if (v.length === 0) return null;
  const mid = Math.floor(v.length/2);
  return v.length % 2 === 1 ? v[mid] : (v[mid-1] + v[mid]) / 2;
}

function clamp01(x:number){ return Math.max(0, Math.min(100, x)); }

export function gpsSignalFromAccuracies(
  rawAcc: Array<number | null | undefined>,
  opts?: {
    maxAcc?: number,        // cap for worst accuracy (meters)
    method?: 'linear'|'exp',
    acceptable?: number,    // meters (used for scale default)
    good?: number
  }
): QualityResult {
  const maxAcc = opts?.maxAcc ?? 100;
  const acceptable = opts?.acceptable ?? 25;
  const good = opts?.good ?? 5;
  const method = opts?.method ?? 'linear';

  const samples = rawAcc
    .map(x => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : Number.NaN))
    .filter((x): x is number => !Number.isNaN(x));

  const n = samples.length;
  if (n === 0) return { score: 0, median: null, samples: 0, pctUnderGood: 0, pctUnderAccept: 0 };

  // compute stats
  const med = median(samples)!;
  const clamped = Math.min(Math.max(med, 0), maxAcc);

  // percent under thresholds
  const pctUnderGood = (samples.filter(s => s <= good).length / n) * 100;
  const pctUnderAccept = (samples.filter(s => s <= acceptable).length / n) * 100;

  // map to 0..100
  let score: number;
  if (method === 'linear') {
    score = 100 * (1 - clamped / maxAcc);
  } else { // 'exp'
    // choose scale so acceptable -> ~50%
    const scale = acceptable / Math.log(2);
    score = 100 * Math.exp(- clamped / scale);
  }
  score = clamp01(score);

  return {
    score: Math.round(score * 100) / 100, // round to 2 decimals
    median: Math.round(med * 100) / 100,
    samples: n,
    pctUnderGood: Math.round(pctUnderGood * 100) / 100,
    pctUnderAccept: Math.round(pctUnderAccept * 100) / 100
  };
}
